import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";

import BreakfastFilters from "./components/BreakfastFilters";
import BreakfastTable from "./components/BreakfastTable";
import BreakfastFormModal from "./components/BreakfastFormModal";
import StatusConfirmModal from "./components/StatusConfirmModal";

import {
  getBreakfastConfigurations,
  getProperties,
  getRoomTypes,
  createBreakfastConfiguration,
  updateBreakfastConfiguration,
  deleteBreakfastConfiguration,
} from "./services/breakfastService";

import {
  EMPTY_FORM,
  DEFAULT_FILTERS,
  toDateInput,
  getErrorMessage,
} from "./breakfastUtils";

const BreakfastManagement = () => {
  const user = useSelector((state) => state.auth.user);
  const currentUserId =
    user?.user?.intUserId || user?.user?.UserID || user?.UserID || 1;

  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState({ totalCount: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [statusRow, setStatusRow] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const loadConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { page, pageSize: 10 };
      if (filters.propertyId) params.propertyId = filters.propertyId;
      if (filters.status !== "") params.status = filters.status;
      if (filters.search.trim()) params.search = filters.search.trim();

      const payload = await getBreakfastConfigurations(params);
      setRows(payload.data || []);
      setPageData({
        totalCount: Number(
          payload.totalCount || payload.data?.[0]?.TotalCount || 0,
        ),
        totalPages: Math.max(1, Number(payload.totalPages || 1)),
      });
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Could not load breakfast configurations.",
        ),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await getProperties();
        setProperties(response.data || []);
      } catch (_) {
        setProperties([]);
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!showForm) return;
    const fetchRoomTypes = async () => {
      try {
        const response = await getRoomTypes(form.intPropertyID);
        setRoomTypes(response.data || []);
      } catch (_) {
        setRoomTypes([]);
      }
    };
    fetchRoomTypes();
  }, [form.intPropertyID, showForm]);

  const pageSummary = useMemo(() => {
    if (!pageData.totalCount) return "No configurations found";
    const start = (page - 1) * 10 + 1;
    return `Showing ${start}–${Math.min(page * 10, pageData.totalCount)} of ${pageData.totalCount}`;
  }, [page, pageData.totalCount]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPage(1);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.intID);
    setForm({
      intPropertyID: String(row.intPropertyID || ""),
      intHotelRoomID: String(row.intHotelRoomID || ""),
      bitBreakfastIncluded: false,
      BreakfastCharge: String(row.BreakfastCharge ?? ""),
      dtmEffectiveFrom: toDateInput(row.dtmEffectiveFrom),
      bitStatus: Boolean(row.bitStatus),
    });
    setFormError("");
    setShowForm(true);
  };

  const updateForm = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const saveConfiguration = async (event) => {
    event.preventDefault();
    if (!form.intPropertyID || !form.intHotelRoomID || !form.dtmEffectiveFrom) {
      setFormError("Property, room type, and effective date are required.");
      return;
    }
    const charge = Number(form.BreakfastCharge);
    if (!Number.isFinite(charge) || charge < 0 || form.BreakfastCharge === "") {
      setFormError("Enter a valid breakfast charge (₹).");
      return;
    }

    const payload = {
      intPropertyID: Number(form.intPropertyID),
      intHotelRoomID: Number(form.intHotelRoomID),
      bitBreakfastIncluded: false,
      BreakfastCharge: charge,
      dtmEffectiveFrom: form.dtmEffectiveFrom,
      bitStatus: form.bitStatus ? 1 : 0,
      ...(editingId
        ? { intUpdatedBy: currentUserId }
        : { intCreatedBy: currentUserId }),
    };

    try {
      setSaving(true);
      setFormError("");
      if (editingId) {
        await updateBreakfastConfiguration(editingId, payload);
      } else {
        await createBreakfastConfiguration(payload);
      }
      setShowForm(false);
      loadConfigurations();
    } catch (requestError) {
      setFormError(
        getErrorMessage(
          requestError,
          "Could not save the breakfast configuration.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleConfigurationStatus = async () => {
    if (!statusRow) return;
    try {
      setTogglingStatus(true);
      if (statusRow.bitStatus) {
        await deleteBreakfastConfiguration(statusRow.intID, {
          hardDelete: false,
          intUpdatedBy: currentUserId,
        });
      } else {
        await updateBreakfastConfiguration(statusRow.intID, {
          intPropertyID: Number(statusRow.intPropertyID),
          intHotelRoomID: Number(statusRow.intHotelRoomID),
          bitBreakfastIncluded: false,
          BreakfastCharge: Number(statusRow.BreakfastCharge || 0),
          dtmEffectiveFrom:
            toDateInput(statusRow.dtmEffectiveFrom) ||
            new Date().toISOString().slice(0, 10),
          bitStatus: 1,
          intUpdatedBy: currentUserId,
        });
      }
      setStatusRow(null);
      loadConfigurations();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          statusRow.bitStatus
            ? "Could not deactivate the breakfast configuration."
            : "Could not enable the breakfast configuration.",
        ),
      );
      setStatusRow(null);
    } finally {
      setTogglingStatus(false);
    }
  };

  return (
    <div className="container-fluid admin-module-page py-4">
      <div className="admin-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div>
          <h3 className="admin-page-title fw-bold mb-1">
            Breakfast Management
          </h3>
        </div>
        <Button
          variant="primary"
          className="admin-page-action"
          onClick={openAdd}
        >
          <FaPlus className="me-2" /> Add Configuration
        </Button>
      </div>

      <BreakfastFilters
        filters={filters}
        properties={properties}
        loading={loading}
        onFilterChange={updateFilter}
        onRefresh={loadConfigurations}
      />

      {error && <div className="alert alert-danger">{error}</div>}

      <BreakfastTable
        rows={rows}
        loading={loading}
        pageSummary={pageSummary}
        page={page}
        totalPages={pageData.totalPages}
        onPageChange={setPage}
        onEdit={openEdit}
        onStatusChange={setStatusRow}
      />

      <BreakfastFormModal
        show={showForm}
        editingId={editingId}
        form={form}
        properties={properties}
        roomTypes={roomTypes}
        saving={saving}
        formError={formError}
        onChange={updateForm}
        onSave={saveConfiguration}
        onClose={() => setShowForm(false)}
      />

      <StatusConfirmModal
        statusRow={statusRow}
        togglingStatus={togglingStatus}
        onConfirm={toggleConfigurationStatus}
        onClose={() => setStatusRow(null)}
      />
    </div>
  );
};

export default BreakfastManagement;
